import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Button } from '../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { SearchableSelect } from '../ui/searchable-select';
import { AlertTriangle, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  GET_ASSIGNABLE_PROJECT_LEADERS,
  UPDATE_PROJECT_MUTATION,
  DELETE_PROJECT_MUTATION,
  GET_PROJECT,
} from '../../graphql/queries';
import {
  PROJECT_STATUS_LABELS,
  PROJECT_STATUS_OPTIONS,
  ProjectStatus,
  compareEmployeesByLastName,
  toDateInputValue,
} from './projectUtils';
import { ProjectColumnsEditor } from './ProjectColumnsEditor';
import { usePermission } from '../../hooks/usePermission';

export interface SettingsProject {
  id: string;
  name: string;
  description?: string | null;
  status: ProjectStatus;
  startDate?: string | null;
  plannedEndDate?: string | null;
  leaderId?: string | null;
  leader?: { id: string; firstName: string; lastName: string } | null;
}

interface ProjectSettingsTabProps {
  project: SettingsProject;
  currentUserId?: string;
}

export const ProjectSettingsTab: React.FC<ProjectSettingsTabProps> = ({
  project,
  currentUserId,
}) => {
  const navigate = useNavigate();
  const canManageAll = usePermission('projects.manage_all');
  const canManageOwn = usePermission('projects.manage_own');
  const canDelete = usePermission('projects.delete');
  const canConfigureKanban = usePermission('kanban.configure');

  const isLeader = !!currentUserId && project.leaderId === currentUserId;
  const canEdit = canManageAll || (canManageOwn && isLeader);
  const canDeleteProject = canManageAll || canDelete;

  const { data: employeesData } = useQuery<{
    assignableProjectLeaders: Array<{
      id: string;
      firstName: string;
      lastName: string;
      isActive: boolean;
    }>;
  }>(GET_ASSIGNABLE_PROJECT_LEADERS, {
    fetchPolicy: 'cache-and-network',
    skip: !canEdit,
  });

  const [updateProject, { loading: saving }] = useMutation(
    UPDATE_PROJECT_MUTATION,
    {
      refetchQueries: [
        { query: GET_PROJECT, variables: { id: project.id } },
        'GetProjectsPaginated',
        'GetMyProjects',
      ],
      awaitRefetchQueries: true,
    },
  );

  const [deleteProject, { loading: deleting }] = useMutation(
    DELETE_PROJECT_MUTATION,
    {
      refetchQueries: ['GetProjectsPaginated', 'GetMyProjects'],
      awaitRefetchQueries: true,
    },
  );

  const [name, setName] = useState(project.name);
  const [description, setDescription] = useState(project.description || '');
  const [status, setStatus] = useState<ProjectStatus>(project.status);
  const [startDate, setStartDate] = useState(toDateInputValue(project.startDate));
  const [plannedEndDate, setPlannedEndDate] = useState(
    toDateInputValue(project.plannedEndDate),
  );
  const [leaderId, setLeaderId] = useState(project.leaderId || '');
  const [errors, setErrors] = useState<{ name?: string; dates?: string }>({});
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);

  useEffect(() => {
    setName(project.name);
    setDescription(project.description || '');
    setStatus(project.status);
    setStartDate(toDateInputValue(project.startDate));
    setPlannedEndDate(toDateInputValue(project.plannedEndDate));
    setLeaderId(project.leaderId || '');
    setErrors({});
  }, [project]);

  const initialState = useMemo(
    () => ({
      name: project.name,
      description: project.description || '',
      status: project.status,
      startDate: toDateInputValue(project.startDate),
      plannedEndDate: toDateInputValue(project.plannedEndDate),
      leaderId: project.leaderId || '',
    }),
    [project],
  );

  const isDirty =
    name !== initialState.name ||
    description !== initialState.description ||
    status !== initialState.status ||
    startDate !== initialState.startDate ||
    plannedEndDate !== initialState.plannedEndDate ||
    leaderId !== initialState.leaderId;

  const activeEmployees = useMemo(() => {
    const filtered = (employeesData?.assignableProjectLeaders || []).filter(
      (e) => e.isActive,
    );
    // Keep the current leader visible even if they no longer have the
    // projects.manage_own permission.
    const leader = project.leader;
    if (leader?.id && !filtered.some((e) => e.id === leader.id)) {
      filtered.push({
        id: leader.id,
        firstName: leader.firstName,
        lastName: leader.lastName,
        isActive: true,
      });
    }
    return filtered.sort(compareEmployeesByLastName);
  }, [employeesData, project]);

  const validate = (): boolean => {
    const errs: typeof errors = {};
    const trimmed = name.trim();
    if (trimmed.length < 2) {
      errs.name = 'Nazwa musi mieć co najmniej 2 znaki';
    } else if (trimmed.length > 200) {
      errs.name = 'Nazwa nie może przekraczać 200 znaków';
    }
    if (startDate && plannedEndDate) {
      if (new Date(startDate) > new Date(plannedEndDate)) {
        errs.dates =
          'Planowana data zakończenia nie może być wcześniejsza niż data rozpoczęcia';
      }
    }
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      await updateProject({
        variables: {
          id: project.id,
          input: {
            name: name.trim(),
            description: description.trim() || null,
            status,
            startDate: startDate || null,
            plannedEndDate: plannedEndDate || null,
            leaderId: leaderId || null,
          },
        },
      });
      toast.success('Projekt zaktualizowany');
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się zapisać projektu');
    }
  };

  const handleDelete = async () => {
    try {
      await deleteProject({ variables: { id: project.id } });
      toast.success('Projekt usunięty');
      setConfirmDeleteOpen(false);
      navigate('/projects');
    } catch (err: any) {
      toast.error(err?.message || 'Nie udało się usunąć projektu');
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Informacje ogólne</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!canEdit && (
            <p className="text-sm text-muted-foreground">
              Brak uprawnień do edycji ustawień. Wymagane: zarządzanie wszystkimi
              projektami lub bycie liderem projektu.
            </p>
          )}

          <div>
            <Label htmlFor="settings-name">
              Nazwa <span className="text-red-500">*</span>
            </Label>
            <Input
              id="settings-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={200}
              disabled={!canEdit}
            />
            {errors.name && (
              <p className="text-xs text-red-600 mt-1">{errors.name}</p>
            )}
          </div>

          <div>
            <Label htmlFor="settings-desc">Opis</Label>
            <Textarea
              id="settings-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={2000}
              disabled={!canEdit}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="settings-status">Status</Label>
              <Select
                value={status}
                onValueChange={(v) => setStatus(v as ProjectStatus)}
                disabled={!canEdit}
              >
                <SelectTrigger id="settings-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROJECT_STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {PROJECT_STATUS_LABELS[s]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="settings-leader">Lider projektu</Label>
              <SearchableSelect
                id="settings-leader"
                value={leaderId}
                onChange={setLeaderId}
                options={activeEmployees.map((emp) => ({
                  value: emp.id,
                  label: `${emp.lastName} ${emp.firstName}`,
                }))}
                placeholder="Wybierz lidera"
                searchPlaceholder="Szukaj po imieniu/nazwisku..."
                disabled={!canEdit}
                clearable
                clearLabel="— brak —"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="settings-start">Data rozpoczęcia</Label>
              <Input
                id="settings-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                disabled={!canEdit}
              />
            </div>
            <div>
              <Label htmlFor="settings-end">Planowana data zakończenia</Label>
              <Input
                id="settings-end"
                type="date"
                value={plannedEndDate}
                onChange={(e) => setPlannedEndDate(e.target.value)}
                disabled={!canEdit}
              />
            </div>
          </div>
          {errors.dates && (
            <p className="text-xs text-red-600">{errors.dates}</p>
          )}

          {canEdit && (
            <div className="flex justify-end gap-2 pt-2 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setName(initialState.name);
                  setDescription(initialState.description);
                  setStatus(initialState.status);
                  setStartDate(initialState.startDate);
                  setPlannedEndDate(initialState.plannedEndDate);
                  setLeaderId(initialState.leaderId);
                  setErrors({});
                }}
                disabled={!isDirty || saving}
              >
                Anuluj zmiany
              </Button>
              <Button onClick={handleSave} disabled={!isDirty || saving}>
                {saving ? 'Zapisywanie...' : 'Zapisz'}
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      <ProjectColumnsEditor
        projectId={project.id}
        canEdit={canConfigureKanban}
      />

      {canDeleteProject && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base text-red-600">
              Strefa niebezpieczna
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground mb-3">
              Usunięcie projektu jest operacją nieodwracalną. Wszystkie kolumny
              i zadania zostaną trwale usunięte.
            </p>
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteOpen(true)}
              className="text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
            >
              <Trash2 className="w-4 h-4 mr-1" />
              Usuń projekt
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={confirmDeleteOpen}
        onOpenChange={(v) => {
          if (!v) setConfirmDeleteOpen(false);
        }}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Usuń projekt
            </DialogTitle>
            <DialogDescription>
              Czy na pewno chcesz usunąć projekt <strong>{project.name}</strong>
              ? Wszystkie kolumny i zadania zostaną trwale usunięte. Tej
              operacji nie można cofnąć.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmDeleteOpen(false)}
              disabled={deleting}
            >
              Anuluj
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? 'Usuwanie...' : 'Usuń projekt'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
